import React, { useState, useRef, useCallback } from "react";
import { Mic, Square, Send, X, Check } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CASES = [
  "Profilo sospeso",
  "Recensione fasulla",
  "Cambio posizione",
  "Numero o GPS errato",
  "Foto rifiutate",
  "Scheda duplicata",
  "Verifiche bloccate",
  "Orari sbagliati",
  "Scheda rimossa",
  "Virtual Tour mancante",
];

export const VoiceNoteRecorder = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [recording, setRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [selected, setSelected] = useState([]);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [duration, setDuration] = useState(0);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  const startRec = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, sampleRate: 16000 }
      });
      const mr = new MediaRecorder(stream, {
        mimeType: 'audio/webm;codecs=opus',
        audioBitsPerSecond: 16000
      });
      chunksRef.current = [];
      mr.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      mr.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: 'audio/webm;codecs=opus' });
        setAudioBlob(blob);
        setAudioUrl(URL.createObjectURL(blob));
        stream.getTracks().forEach(t => t.stop());
      };
      mediaRef.current = mr;
      mr.start(1000);
      setRecording(true);
      setDuration(0);
      timerRef.current = setInterval(() => setDuration(d => d + 1), 1000);
    } catch {
      alert("Permesso microfono negato. Abilita il microfono nelle impostazioni del browser.");
    }
  }, []);

  const stopRec = useCallback(() => {
    if (mediaRef.current && mediaRef.current.state === "recording") {
      mediaRef.current.stop();
    }
    setRecording(false);
    clearInterval(timerRef.current);
  }, []);

  const sendNote = useCallback(async () => {
    if (!audioBlob) return;
    setSending(true);
    const fd = new FormData();
    fd.append("audio", audioBlob, "nota_vocale.webm");
    fd.append("cases", selected.join(", "));
    try {
      await fetch(`${API_URL}/api/voice-note`, { method: "POST", body: fd });
      setSent(true);
      setTimeout(() => {
        setSent(false); setAudioBlob(null); setAudioUrl(null);
        setSelected([]); setIsOpen(false); setDuration(0);
      }, 2500);
    } catch { alert("Errore invio. Riprova."); }
    finally { setSending(false); }
  }, [audioBlob, selected]);

  const reset = () => { setAudioBlob(null); setAudioUrl(null); setDuration(0); };
  const toggle = (c) => setSelected(s => s.includes(c) ? s.filter(x => x !== c) : [...s, c]);
  const fmt = (s) => `${Math.floor(s/60)}:${String(s%60).padStart(2,'0')}`;

  return (
    <>
      {/* Panel */}
      {isOpen && (
        <div data-testid="voice-recorder-panel" className="fixed bottom-32 sm:bottom-28 left-3 sm:left-4 z-50 w-[300px] sm:w-[320px] bg-[#0c1220] border border-white/10 rounded-2xl shadow-2xl overflow-hidden" style={{ animation: "fadeInUp 0.3s ease-out" }}>
          {/* Header */}
          <div className="bg-[#EA4335] px-4 py-2.5 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Mic className="w-4 h-4 text-white" />
              <span className="text-white font-bold text-sm">Nota Vocale</span>
            </div>
            <button data-testid="voice-recorder-close" onClick={() => { stopRec(); setIsOpen(false); reset(); }} className="text-white/70 hover:text-white">
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Cases checklist */}
          <div className="px-3 py-3 border-b border-white/10">
            <p className="text-gray-400 text-xs mb-2 font-bold">Seleziona il tuo problema:</p>
            <div className="grid grid-cols-2 gap-1.5 max-h-[140px] overflow-y-auto">
              {CASES.map(c => (
                <button key={c} onClick={() => toggle(c)}
                  data-testid={`voice-case-${c.toLowerCase().replace(/\s+/g, '-')}`}
                  className={`flex items-center gap-1.5 text-left px-2 py-1.5 rounded-lg text-xs transition-all ${
                    selected.includes(c)
                      ? 'bg-[#4285F4]/20 text-white border border-[#4285F4]/40'
                      : 'bg-white/5 text-gray-400 border border-white/5 hover:border-white/20'
                  }`}>
                  <div className={`w-3.5 h-3.5 rounded flex-shrink-0 flex items-center justify-center ${
                    selected.includes(c) ? 'bg-[#4285F4]' : 'border border-gray-500'
                  }`}>
                    {selected.includes(c) && <Check className="w-2.5 h-2.5 text-white" />}
                  </div>
                  <span className="truncate">{c}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Recorder */}
          <div className="px-4 py-4 flex flex-col items-center gap-3">
            {sent ? (
              <div data-testid="voice-recorder-sent" className="flex items-center gap-2 text-[#34A853]">
                <Check className="w-5 h-5" />
                <span className="font-bold text-sm">Inviata! Ti ricontatto presto.</span>
              </div>
            ) : audioBlob ? (
              <>
                <audio data-testid="voice-recorder-audio" src={audioUrl} controls className="w-full h-8" style={{ filter: 'invert(1)' }} />
                <div className="flex gap-2 w-full">
                  <button data-testid="voice-recorder-rerecord" onClick={reset}
                    className="flex-1 bg-white/10 hover:bg-white/20 text-white rounded-full py-2 text-xs font-bold transition-colors">
                    Riregistra
                  </button>
                  <button data-testid="voice-recorder-send" onClick={sendNote} disabled={sending}
                    className="flex-1 bg-[#34A853] hover:bg-[#2d9249] text-white rounded-full py-2 text-xs font-bold transition-colors inline-flex items-center justify-center gap-1 disabled:opacity-50">
                    <Send className="w-3.5 h-3.5" /> {sending ? "Invio..." : "Invia"}
                  </button>
                </div>
                <p className="text-gray-500 text-[10px]">Durata: {fmt(duration)} — Compressa opus/webm</p>
              </>
            ) : (
              <>
                <button data-testid="voice-recorder-rec-btn" onClick={recording ? stopRec : startRec}
                  className={`w-16 h-16 rounded-full flex items-center justify-center transition-all ${
                    recording ? 'bg-[#EA4335] animate-pulse scale-110' : 'bg-white/10 hover:bg-white/20'
                  }`}>
                  {recording ? <Square className="w-6 h-6 text-white" /> : <Mic className="w-7 h-7 text-white" />}
                </button>
                <p className="text-gray-400 text-xs">
                  {recording ? `Registrando... ${fmt(duration)}` : "Premi per registrare"}
                </p>
                {recording && (
                  <div className="flex items-center gap-1.5">
                    <div className="w-2 h-2 rounded-full bg-[#EA4335] animate-pulse"></div>
                    <span className="text-[#EA4335] text-xs font-bold">REC</span>
                  </div>
                )}
              </>
            )}
          </div>

          <p className="text-center text-gray-600 text-[9px] pb-2 px-3">
            Spiega a parole tue cosa è successo. Nessun tecnicismo, parla come a un amico.
          </p>
        </div>
      )}

      {/* REC FAB Button */}
      <button
        data-testid="voice-recorder-fab"
        onClick={() => { setIsOpen(!isOpen); if (!isOpen) { setSent(false); reset(); } }}
        className={`fixed bottom-[7.5rem] sm:bottom-[5.5rem] left-4 sm:left-6 z-50 w-12 h-12 rounded-full shadow-xl flex items-center justify-center transition-all duration-300 hover:scale-110 ${
          isOpen ? 'bg-[#EA4335]' : 'bg-[#EA4335]/90 hover:bg-[#EA4335]'
        }`}>
        <Mic className="w-5 h-5 text-white" />
        {!isOpen && <div className="absolute -top-0.5 -right-0.5 w-3 h-3 bg-red-500 rounded-full border-2 border-[#0c1220] animate-pulse"></div>}
        {!isOpen && <span className="absolute -bottom-4 text-[7px] font-black text-[#EA4335]">REC</span>}
      </button>
    </>
  );
};
